import { User, CreateUser, UserItem, Location } from "../models/Models";

import { IHttpAnswer } from "../interfaces/httpAnswer";
import * as versionFile from "../version.json";
import * as localStorageKeys from "../constants/localStorageKeys";

const API_URL = process.env.REACT_APP_API_URL;

async function login(
  mobile: string,
  location: string,
  loginType: string
): Promise<IHttpAnswer<User>> {
  const version = `${versionFile.major}.${versionFile.minor}`;
  const loginRequest = {
    phoneNumber: mobile,
    location: location,
    version: version,
    loginType: loginType,
  };
  const response = await fetch(`${API_URL}/Authentication/GetUserAccess`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(loginRequest),
  });
  if (response.ok) {
    const data = await response.json();
    return { data: data, isOk: response.ok, statusCode: response.status };
  }
  const data = await response.json();
  return { data: data, isOk: response.ok, statusCode: response.status };
}

async function logout(): Promise<IHttpAnswer<boolean>> {
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(
    `${API_URL}/Authentication/InvalidateCurrentSession`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionId}`,
      },
    }
  );
  return { data: true, isOk: response.ok, statusCode: response.status };
}

async function getLoginLocations(): Promise<IHttpAnswer<Location[]>> {
  const response = await fetch(`${API_URL}/Authentication/GetLoginLocations`);

  if (response.ok) {
    const data = await response.json();
    const locations = data.locations.map(
      (location: any) => new Location(location)
    );
    return {
      data: locations,
      isOk: response.ok,
      statusCode: response.status,
    };
  }

  return { data: [], isOk: response.ok, statusCode: response.status };
}

async function updateTravelDetails(barCode: string, text: string) {
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(
    `${API_URL}/Passenger/UpdateTravelDetails/${barCode}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionId}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(text),
    }
  );

  if (response.ok) {
    return {
      isOk: response.ok,
      statusCode: response.status,
    };
  }
  return { isOk: response.ok, statusCode: response.status };
}

async function createUser(ssn: string, user: CreateUser) {
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(`${API_URL}/Admin/AddUser/${ssn}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionId}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(user),
  });

  if (response.ok) {
    return {
      isOk: response.ok,
      statusCode: response.status,
    };
  }
  return { isOk: response.ok, statusCode: response.status };
}

async function editUser(ssn: string, user: CreateUser) {
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(`${API_URL}/Admin/UpdateUser/${ssn}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${sessionId}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(user),
  });

  if (response.ok) {
    return {
      isOk: response.ok,
      statusCode: response.status,
    };
  }
  return { isOk: response.ok, statusCode: response.status };
}

async function getAllUsers(): Promise<IHttpAnswer<Array<UserItem>>> {
  const userList = new Array<UserItem>();
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(`${API_URL}/Admin/GetUsers`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${sessionId}`,
      "Content-Type": "application/json",
    },
  });
  if (response.status === 200) {
    const data: { users: any[] } = await response.json();
    data.users.map((item) => userList.push(new UserItem(item)));
  }
  return {
    data: userList,
    isOk: response.ok,
    statusCode: response.status,
  };
}

async function deleteSelectedUser(ssn: string) {
  const sessionId = new User(
    JSON.parse(localStorage.getItem(localStorageKeys.USER) ?? "{}") ?? {}
  ).encodedSessionId;

  const response = await fetch(`${API_URL}/Admin/DeleteUser/${ssn}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${sessionId}`,
      "Content-Type": "application/json",
    },
  });

  if (response.ok) {
    return {
      isOk: response.ok,
      statusCode: response.status,
    };
  }
  return { isOk: response.ok, statusCode: response.status };
}

export {
  login,
  logout,
  getLoginLocations,
  createUser,
  editUser,
  getAllUsers,
  deleteSelectedUser,
  updateTravelDetails,
};
